import { useState } from "react";
import { useTranslate } from "ra-core";
import { InputAdornment, IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Loop from "@mui/icons-material/Loop";
import { useFormContext } from "react-hook-form";
import { TextInput, TextInputProps, useNotify } from "react-admin";
import generator from "secure-random-password";
import useCopyToClipboard from "./useCopyToClipboard";

export const PasswordInput = (props: PasswordInputProps) => {
  const { initiallyVisible = false, helperText, ...rest } = props;
  const [visible, setVisible] = useState(initiallyVisible);
  const translate = useTranslate();

  const handleClick = () => {
    setVisible(!visible);
  };

  const { setValue } = useFormContext();
  const notify = useNotify();
  const [, copy] = useCopyToClipboard();

  const handleGeneratePassword = async () => {
    const password = generator.randomPassword({
      length: 16,
      characters: [generator.lower, generator.upper, generator.digits],
    });
    setValue("password", password, { shouldDirty: true });

    if (await copy(password)) {
      notify(translate("common.passwordGeneratedAndCopied"), {
        type: "info",
      });
    } else {
      setVisible(true);
      notify(translate("common.passwordNotCopied"), {
        type: "error",
      });
    }
  };

  return (
    <TextInput
      type={visible ? "text" : "password"}
      size="small"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label={translate(
                visible
                  ? "ra.input.password.toggle_visible"
                  : "ra.input.password.toggle_hidden"
              )}
              onClick={handleClick}
              size="large"
            >
              {visible ? <Visibility /> : <VisibilityOff />}
            </IconButton>
            <IconButton
              aria-label={translate("common.generatePassword")}
              onClick={handleGeneratePassword}
              size="large"
            >
              <Loop />
            </IconButton>
          </InputAdornment>
        ),
      }}
      helperText={
        <>
          {helperText && (
            <>
              {helperText}
              <br />
            </>
          )}
          {translate("common.generatePasswordWillCopyToClipboard")}
        </>
      }
      {...rest}
    />
  );
};

export interface PasswordInputProps extends TextInputProps {
  initiallyVisible?: boolean;
}
